// components/common/Button.tsx
import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xs';
  icon?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  children,
  className,
  disabled,
  ...props
}) => {
const baseStyles = `
  inline-flex sm:inline-flex items-center justify-center
  font-medium transition-colors
  focus:outline-none focus:ring-2 focus:ring-offset-2
  rounded-lg
  px-1 py-1 sm:px-1 sm:py-1.5
  text-xs sm:text-xs
  space-x-1 sm:space-x-0.5
  whitespace-nowrap
`;  
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-white border border-blue-700 text-blue-700 hover:bg-gray-50 focus:ring-gray-500',
    danger: ' text-red-600 border border-red-400 hover:bg-red-100 focus:ring-red-500',
  };

  const sizes = {
    xs:'px-1.5 sm:px-1.5 py-3 text-xs',
    sm: 'px-2 sm:px-3 py-3 text-xs sm:text-sm',
    md: 'px-3 sm:px-4 py-2 text-sm',
    lg: 'px-4 sm:px-6 py-3 text-base',
  };

  return (
    <button
      className={clsx(
        baseStyles,
        variants[variant],
        sizes[size],
        {
          'opacity-50 cursor-not-allowed': disabled || loading,
        },
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : icon ? (
        <span className="mr-1 sm:mr-2">{icon}</span>
      ) : null}
      {children}
    </button>
  );
};